import isFunction from 'lodash/isFunction';
import React, {
  useRef,
  RefObject,
  useEffect,
  ReactElement,
  useCallback,
} from 'react';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import {
  useVirtualizer,
  VirtualItem,
} from '@tanstack/react-virtual';

const DEFAULT_ESTIMATE_SIZE = 88;

type Props<T> = {
  renderItemComponent: (props: T, index: number) => ReactElement<T>;
  data: T[];
  scrollToBottom?: boolean;
  overscan?: number;
  className?: string;
  scrollContainerClassName?: string;
  rowClassName?: string | ((item: T) => string);
  gridClassName?: string;
  itemSize?: number;
  onEndReached?: () => void;
  endReachedOffset?: number;
  onScrollElementRef?: (el: HTMLDivElement | null) => void;
  endTriggerOnceUntilDataChange?: boolean;
};
export default function VirtualList<T>({
  renderItemComponent,
  data,
  overscan = 5,
  className,
  scrollContainerClassName,
  itemSize = undefined,
  rowClassName,
  gridClassName,
  scrollToBottom = false,
  onEndReached,
  endReachedOffset = 300,
  onScrollElementRef,
  endTriggerOnceUntilDataChange = true,
}: Props<T>) {
  const parentRef = useRef<HTMLElement>(null) as RefObject<HTMLDivElement>;
  const endFiredRef = useRef<boolean>(false);
  const lastCountRef = useRef<number>(0);

  // Detect if we're using grid layout
  const isGridLayout = gridClassName?.includes('grid');

  const virtualizer = useVirtualizer({
    getScrollElement: () => parentRef.current,
    count: data.length,
    estimateSize: () => itemSize ?? DEFAULT_ESTIMATE_SIZE,
    overscan: isGridLayout ? data.length : overscan, // Show all items for grid
  });

  const { getVirtualItems, getTotalSize, scrollToOffset } = virtualizer;

  const virtualItems = getVirtualItems();
  const totalSize = getTotalSize();

  const classes = clsx(
    'relative flex flex-col flex-1 overflow-hidden',
    className,
  );

  const scrollContainerClasses = clsx(
    'populate-virtual-list-scroll relative w-full h-full overflow-y-auto overflow-x-hidden',
    scrollContainerClassName,
  );

  const gridClasses = clsx(
    gridClassName,
    { 'absolute top-0 left-0 w-full': isGridLayout },
  );

  useEffect(() => {
    if (data?.length && scrollToBottom) {
      scrollToOffset(totalSize, { align: 'end' });
    }
  }, [data, totalSize, scrollToBottom]);

  // Expose inner scroll element ref to parent if requested
  useEffect(() => {
    if (onScrollElementRef){
      onScrollElementRef(parentRef.current);}
    return () => { onScrollElementRef?.(null); };
  }, [onScrollElementRef]);

  // Trigger onEndReached when close to bottom of the scroll container
  const handleEndReachedCheck = useCallback(() => {
    const el = parentRef.current;
    if (el && onEndReached) {
      const { scrollTop, clientHeight, scrollHeight } = el;
      const remaining = scrollHeight - (scrollTop + clientHeight);
      if (remaining <= endReachedOffset) {
        if (endTriggerOnceUntilDataChange && endFiredRef.current) {
          // already fired and no new data yet; do nothing
          return;
        }
        endFiredRef.current = true;
        onEndReached();
      }
    }
  }, [onEndReached, endReachedOffset, endTriggerOnceUntilDataChange]);

  useEffect(() => {
    const el = parentRef.current;

    const handleScroll = () => { handleEndReachedCheck(); };

    if (el && onEndReached) {
      el.addEventListener('scroll', handleScroll, { passive: true });
      // Call once in case content doesn't overflow
      handleEndReachedCheck();
    }
    return () => {
      if (el && onEndReached) {
        el.removeEventListener('scroll', handleScroll);
      }
    };
  }, [onEndReached, endReachedOffset, data, endTriggerOnceUntilDataChange, handleEndReachedCheck]);

  // Reset the one-shot guard when data length changes (new items appended or filters changed)
  useEffect(() => {
    const nextCount = data?.length ?? 0;
    if (nextCount !== lastCountRef.current) {
      lastCountRef.current = nextCount;
      endFiredRef.current = false;
    }
  }, [data?.length]);

  return data.length > 0 ? (
    <div className={classes}>
      <div
        className={scrollContainerClasses}
        style={{ scrollbarGutter: 'stable' }}
        ref={parentRef}
      >
        <div
          style={{ height: isGridLayout ? 'auto' : totalSize }}
          className={clsx('w-full', { relative: !isGridLayout })}
        >
          <div
            className={gridClasses}
            style={{
              transform: isGridLayout ? undefined : `translateY(${(virtualItems[0]?.start ?? 0)}px)`,
            }}
          >
            {virtualItems.map((virtualItem: VirtualItem) => (
              <div
                ref={virtualizer.measureElement}
                key={virtualItem.key}
                data-index={virtualItem.index}
                className={clsx(
                  'w-full relative',
                  isFunction(rowClassName)
                    ? rowClassName(data[virtualItem.index] as T)
                    : rowClassName,
                )}
                style={{
                  height: !isNil(itemSize) ? `${itemSize}px` : undefined,
                }}
              >
                {renderItemComponent(data[virtualItem.index] as T, virtualItem.index)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
